import {
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/client/react";

import { Direction } from "@lytra/frontend-common/__generated__/graphql";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@lytra/frontend-common/components/v2/ui/dialog";
import { Alert } from "@lytra/frontend-common/components/v2/ui/alert";
import { Button } from "@lytra/frontend-common/components/v2/ui/button";
import { Checkbox } from "@lytra/frontend-common/components/v2/ui/checkbox";
import { Field } from "@lytra/frontend-common/components/v2/ui/field";
import { Icon } from "@lytra/frontend-common/components/v2/ui/icon";
import { Textarea } from "@lytra/frontend-common/components/v2/ui/textarea";
import { useProfile } from "@/providers/ProfileProvider";
import type { PickerAgent } from "./MentionList";
import { RecipientField } from "./RecipientField";
import {
  addRecipient,
  channelSummary,
  classifyRecipient,
  isAddressShaped,
  toRecipientInputs,
  type AgentsState,
} from "./recipients";
import {
  TICKET_FORWARD_MAIL_ENABLED,
  TICKET_FORWARD_TENANT_DOMAINS,
  TICKET_FORWARD_TO_COLLEAGUES,
} from "./forwardQueries";
import {
  afterForwardRefusal,
  forwardErrorKind,
  forwardTextLength,
  keyForForwardSubmit,
  MAX_FORWARD_TEXT_CHARS,
  useForwardDrafts,
  type ForwardDraft,
  type ForwardDrafts,
  type ForwardErrorKind,
} from "./forwardDraft";
import type { TicketMessage } from "./noteThreads";
import { personName } from "./ticketHelpers";

/** What the dialog reads of a message: its id, subject, sender and attachments. */
export type ForwardMessage = Pick<
  TicketMessage,
  "id" | "direction" | "emailSubject" | "contact" | "agent" | "attachments"
>;

/** The counter under the message shows from here on; the limit itself is MAX_FORWARD_TEXT_CHARS. */
const TEXT_COUNTER_FROM = 4000;

const SIZE_FORMAT = new Intl.NumberFormat("de-DE", {
  maximumFractionDigits: 1,
});

/** "840 B", "320 KB", "1,4 MB": binary steps like the attachment list of the ticket. */
function formatSize(bytes: number): string {
  if (bytes < 1024) return `${SIZE_FORMAT.format(bytes)} B`;
  if (bytes < 1024 * 1024)
    return `${SIZE_FORMAT.format(Math.round(bytes / 1024))} KB`;
  return `${SIZE_FORMAT.format(bytes / (1024 * 1024))} MB`;
}

/** The subject the mail will carry: "WG: " plus the original without line breaks (as the server builds it, D-08). */
function forwardSubject(original: string | null | undefined): string | null {
  const cleaned = (original ?? "").replace(/[\r\n]/g, "").trim();
  return cleaned === "" ? null : cleaned;
}

/** Who wrote the message: the contact of an inbound mail, the agent of an outbound one. */
function senderName(message: ForwardMessage): string | null {
  const person =
    message.direction === Direction.Outbound ? message.agent : message.contact;
  const name =
    person && "name" in person && person.name
      ? person.name
      : personName(person ?? undefined);
  return name ?? person?.emailAddress ?? null;
}

export interface ForwardDialogProps {
  message: ForwardMessage | null;
  conversationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The tenant's colleagues (the `agents` query of the ticket). */
  agents: readonly PickerAgent[];
  agentsState: AgentsState;
  /** Called once after a successful send with the number of recipients. */
  onSent: (count: number) => void;
  /** The trigger that opened the dialog; focus returns here on close. */
  returnFocusRef?: RefObject<HTMLElement | null>;
}

/**
 * "An Kollegen senden" (#717, D-05 to D-10, UI-SPEC section 2): the dialog that composes the
 * recipient field with the message and sends it through `forwardToColleagues`. The content is
 * unmounted while the dialog is closed, so the form below starts fresh at every open and mints
 * its idempotency key then.
 */
export function ForwardDialog({
  message,
  conversationId,
  open,
  onOpenChange,
  agents,
  agentsState,
  onSent,
  returnFocusRef,
}: ForwardDialogProps) {
  const { t } = useTranslation();
  // One draft per message while the ticket view is mounted (UI-SPEC "Draft handling").
  const drafts = useForwardDrafts();
  // While a send is in flight the dialog cannot be dismissed, so the payload cannot drift from
  // the key being sent (T-03-32): Escape, overlay click and the X are all ignored.
  const [pending, setPending] = useState(false);
  // Asked once while the ticket view is mounted. Until the answer is in (or when it fails) no
  // mail is offered: the server refuses mail recipients while it is off anyway.
  const { data: mailData } = useQuery(TICKET_FORWARD_MAIL_ENABLED, {
    errorPolicy: "all",
  });
  const mailEnabled = mailData?.forwardMailEnabled ?? false;
  return (
    <Dialog
      open={open && message !== null}
      onOpenChange={(next) => {
        if (!next && pending) return;
        onOpenChange(next);
      }}
    >
      <DialogContent
        size="lg"
        onEscapeKeyDown={(event) => {
          if (pending) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (pending) event.preventDefault();
        }}
        onCloseAutoFocus={(event) => {
          const target = returnFocusRef?.current;
          if (target) {
            event.preventDefault();
            target.focus();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>{t("tickets.forward.title")}</DialogTitle>
          <DialogDescription>
            {t(
              mailEnabled
                ? "tickets.forward.description"
                : "tickets.forward.descriptionLytraOnly",
            )}
          </DialogDescription>
        </DialogHeader>
        {message && (
          <ForwardForm
            key={message.id}
            message={message}
            conversationId={conversationId}
            drafts={drafts}
            pending={pending}
            onPendingChange={setPending}
            onClose={() => onOpenChange(false)}
            agents={agents}
            agentsState={agentsState}
            mailEnabled={mailEnabled}
            onSent={onSent}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface ForwardFormProps {
  message: ForwardMessage;
  conversationId: string;
  drafts: ForwardDrafts;
  pending: boolean;
  onPendingChange: (pending: boolean) => void;
  /** Closes the dialog for good, also while `pending` has just ended. */
  onClose: () => void;
  agents: readonly PickerAgent[];
  agentsState: AgentsState;
  /** False while sending by mail is off: colleagues only, no mail copy. */
  mailEnabled: boolean;
  onSent: (count: number) => void;
}

function ForwardForm({
  message,
  conversationId,
  drafts,
  pending,
  onPendingChange,
  onClose,
  agents,
  agentsState,
  mailEnabled,
  onSent,
}: ForwardFormProps) {
  const { t } = useTranslation();
  const { user } = useProfile();
  const { data: tenantData } = useQuery(TICKET_FORWARD_TENANT_DOMAINS, {
    errorPolicy: "all",
  });
  const [forward] = useMutation(TICKET_FORWARD_TO_COLLEAGUES, {
    errorPolicy: "none",
    refetchQueries: ["Ticket"],
  });

  // The dialog's content is unmounted while closed: reopening on this message restores the
  // draft (recipients, text, attachment choice, key), another message starts empty.
  const [draft, setDraftState] = useState<ForwardDraft>(() =>
    drafts.get(message.id),
  );
  const draftRef = useRef(draft);
  const setDraft = (next: ForwardDraft) => {
    draftRef.current = next;
    setDraftState(next);
    drafts.set(message.id, next);
  };
  const { recipients, text, includeAttachments } = draft;
  const textLength = forwardTextLength(text);
  const [recipientError, setRecipientError] = useState<string | null>(null);
  const [textError, setTextError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<ForwardErrorKind | null>(null);
  const sendingRef = useRef(false);
  const recipientInputRef = useRef<HTMLInputElement>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const attachmentsRef = useRef<HTMLButtonElement>(null);
  const summaryId = useId();

  // After a "too large" refusal the way out is the attachment checkbox; it is read-only while
  // the send is in flight, so the focus moves once the fields are editable again.
  useEffect(() => {
    if (sendError === "tooLarge" && !pending) attachmentsRef.current?.focus();
  }, [sendError, pending]);

  const internalDomains = tenantData?.tenant?.internalDomains ?? null;
  const counts = channelSummary(
    recipients.map((entry) =>
      classifyRecipient(entry, agents, agentsState, internalDomains),
    ),
  );
  const chips = counts.lytra + counts.mail + counts.unknown;
  // Mail-only blocks are removed, not disabled, while no chip leaves Lytra (UI-SPEC 2), and
  // always while sending by mail is off.
  const mailBlocks = mailEnabled && (counts.mail > 0 || counts.unknown > 0);
  const lytraOnly = chips > 0 && !mailBlocks;

  // The summary weighs Lytra against mail: with mail off there is nothing to weigh.
  const summary =
    chips === 0 || !mailEnabled
      ? null
      : counts.unknown > 0
        ? t("tickets.forward.summary.unknown")
        : counts.mail === 0
          ? t("tickets.forward.summary.lytraOnly")
          : counts.lytra === 0
            ? t("tickets.forward.summary.mailOnly")
            : t("tickets.forward.summary.mixed", {
                lytra: counts.lytra,
                mail: counts.mail,
              });

  const files = message.attachments.edges
    .map((edge) => edge.node)
    .filter((attachment) => !attachment.isInline);
  const totalSize = formatSize(
    files.reduce((sum, file) => sum + (file.size ?? 0), 0),
  );
  const subject = forwardSubject(message.emailSubject);
  const subjectText = subject
    ? t("tickets.forward.subject.value", { subject })
    : t("tickets.forward.subject.none");
  const sender = senderName(message);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (sendingRef.current) return;

    // Validation runs on submit only. Blur has already turned valid text in the recipient input
    // into a chip; what is left there is invalid, unless nothing blurred it (then it is added
    // here, nothing typed is silently dropped).
    let list = draftRef.current.recipients;
    const leftover = recipientInputRef.current?.value.trim() ?? "";
    if (leftover !== "") {
      const added = isAddressShaped(leftover)
        ? addRecipient(
            list,
            { address: leftover },
            {
              viewerId: user.id,
              viewerEmail: user.emailAddress,
              agents,
              mailEnabled,
            },
          )
        : null;
      if (!added || added.error) {
        setRecipientError(
          added?.error === "self"
            ? t("tickets.forward.errors.self")
            : added?.error === "cap"
              ? t("tickets.forward.errors.cap")
              : added?.error === "noAccount"
                ? t("tickets.forward.errors.noAccount", { input: leftover })
                : t("tickets.forward.errors.invalidAddress", {
                    input: leftover,
                  }),
        );
        recipientInputRef.current?.focus();
        return;
      }
      list = added.list;
    }
    if (list.length === 0) {
      setRecipientError(t("tickets.forward.errors.noRecipient"));
      recipientInputRef.current?.focus();
      return;
    }
    if (forwardTextLength(draftRef.current.text) > MAX_FORWARD_TEXT_CHARS) {
      setTextError(t("tickets.forward.errors.tooLong"));
      textRef.current?.focus();
      return;
    }
    setRecipientError(null);
    setTextError(null);

    // The key comes from the draft, never from the click: an unchanged retry is the same send.
    const { clientRequestId, draft: keyed } = keyForForwardSubmit({
      ...draftRef.current,
      recipients: list,
    });
    setDraft(keyed);

    sendingRef.current = true;
    onPendingChange(true);
    setSendError(null);
    try {
      await forward({
        variables: {
          conversationId,
          messageId: message.id,
          recipients: toRecipientInputs(keyed.recipients, agents),
          clientRequestId,
          text: keyed.text,
          includeAttachments: keyed.includeAttachments,
        },
      });
    } catch (error) {
      // Every input stays; the payload is remembered so only a change mints a new key (a
      // reused-key refusal mints one right away, the old key can never succeed).
      const kind = forwardErrorKind(error);
      setDraft(afterForwardRefusal(draftRef.current, kind));
      setSendError(kind);
      return;
    } finally {
      sendingRef.current = false;
      onPendingChange(false);
    }
    drafts.clear(message.id);
    onClose();
    onSent(keyed.recipients.length);
  };

  return (
    <form
      className="contents"
      onSubmit={(event) => void submit(event)}
      onKeyDown={(event) => {
        // Enter in the recipient field adds a recipient (or does nothing); it never sends.
        if (event.key === "Enter" && event.target === recipientInputRef.current) {
          event.preventDefault();
        }
      }}
    >
      <DialogBody>
        <div className="flex flex-col gap-1">
          <Field
            label={t("tickets.forward.recipients.label")}
            error={recipientError}
          >
            <RecipientField
              value={recipients}
              onChange={(next) => setDraft({ ...draftRef.current, recipients: next })}
              agents={agents}
              agentsState={agentsState}
              internalDomains={internalDomains}
              viewerId={user.id}
              viewerEmail={user.emailAddress}
              error={recipientError}
              onError={setRecipientError}
              inputRef={recipientInputRef}
              disabled={pending}
              describedById={summary ? summaryId : undefined}
              mailEnabled={mailEnabled}
            />
          </Field>
          {summary && (
            <p
              id={summaryId}
              aria-live="polite"
              className="text-caption text-ink-muted"
            >
              {summary}
            </p>
          )}
        </div>

        <Field
          label={t("tickets.forward.message.label")}
          hint={t("tickets.forward.message.hint")}
          optional
          error={textError}
          count={textLength >= TEXT_COUNTER_FROM ? textLength : undefined}
          maxCount={MAX_FORWARD_TEXT_CHARS}
        >
          <Textarea
            ref={textRef}
            value={text}
            readOnly={pending}
            onChange={(event) => {
              if (textError) setTextError(null);
              setDraft({ ...draftRef.current, text: event.target.value });
            }}
            className="min-h-20"
          />
        </Field>

        {mailBlocks && (
          <>
            <div className="flex items-baseline gap-3">
              <span className="text-label text-ink">
                {t("tickets.forward.subject.label")}
              </span>
              <span
                className="line-clamp-2 min-w-0 text-body text-ink-muted"
                title={subjectText}
              >
                {subjectText}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <h3 className="text-label text-ink">
                {t("tickets.forward.included.heading")}
              </h3>
              <ul className="flex flex-col gap-1 text-caption text-ink-muted">
                <IncludedLine>
                  {user.emailSignature?.trim()
                    ? t("tickets.forward.included.signature")
                    : t("tickets.forward.included.signatureFallback")}
                </IncludedLine>
                <IncludedLine note={t("tickets.forward.included.linkNote")}>
                  {t("tickets.forward.included.link")}
                </IncludedLine>
                <IncludedLine>
                  {sender
                    ? t("tickets.forward.included.message", { sender })
                    : t("tickets.forward.included.messageNoSender")}
                </IncludedLine>
              </ul>
            </div>
          </>
        )}

        {files.length > 0 && mailBlocks && (
          <Field
            orientation="inline"
            label={t("tickets.forward.attachments.label")}
            hint={t(
              files.length === 1
                ? "tickets.forward.attachments.hintOne"
                : "tickets.forward.attachments.hintOther",
              { count: files.length, size: totalSize },
            )}
          >
            <Checkbox
              ref={attachmentsRef}
              checked={includeAttachments}
              disabled={pending}
              onCheckedChange={(next) =>
                setDraft({
                  ...draftRef.current,
                  includeAttachments: next === true,
                })
              }
            />
          </Field>
        )}
        {files.length > 0 && lytraOnly && (
          <p className="text-caption text-ink-muted">
            {t("tickets.forward.attachments.lytraOnly")}
          </p>
        )}
        {sendError && (
          <Alert tone="error">
            {t(
              sendError === "gone"
                ? "tickets.forward.errors.gone"
                : sendError === "tooLarge"
                  ? "tickets.forward.errors.tooLarge"
                  : sendError === "keyReused"
                    ? "tickets.forward.errors.changed"
                    : sendError === "mailUnavailable"
                      ? "tickets.forward.errors.mailUnavailable"
                      : "tickets.forward.errors.sendFailed",
            )}
          </Alert>
        )}
      </DialogBody>
      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          disabled={pending}
          onClick={onClose}
        >
          {t("tickets.forward.actions.cancel")}
        </Button>
        <Button type="submit" loading={pending}>
          {t("tickets.forward.actions.send")}
        </Button>
      </DialogFooter>
    </form>
  );
}

/** One line of "Wird mitgesendet": a check, the text and an optional second caption line. */
function IncludedLine({
  children,
  note,
}: {
  children: ReactNode;
  note?: string;
}) {
  return (
    <li className="flex items-start gap-2">
      <Icon icon={Check} size={12} className="mt-0.5 text-ink-muted" />
      <span className="flex min-w-0 flex-col">
        <span>{children}</span>
        {note && <span>{note}</span>}
      </span>
    </li>
  );
}
